import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, AlertCircle, Sparkles, ShieldCheck, 
  Zap, ArrowRight, Download, Search, LayoutDashboard,
  Shield, Rocket, HelpCircle, ChevronDown, Coins
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCurrency } from '../contexts/CurrencyContext';
import RechargeCard from '../components/ui/RechargeCard';
import { API_URL } from '../services/api';

export default function Billing() {
  const { user, updateUser } = useAuth();
  const { currentCurrency, setCurrency, prices } = useCurrency();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const credits = params.get('credits');
    
    if (paymentStatus === 'success') {
      let message = 'Payment successful! Your credits have been securely deposited.';
      if (credits) {
        message = `Success! Added ${credits} glowing new credits to your account. ✨`;
      }
      setSuccess(message);
      window.history.replaceState({}, document.title, window.location.pathname);
      refreshCredits();
    } else if (paymentStatus === 'cancelled') {
      setError('Payment process was cancelled safely.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const refreshCredits = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/credits/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.success && result.data.creditsRemaining !== undefined) {
        updateUser({ creditsRemaining: result.data.creditsRemaining });
      }
    } catch (err) {
      console.error('Failed to refresh credits:', err);
    }
  };

  const handlePurchase = async (selectedCredits: number) => {
    try {
      if (!user) {
        setError('Please log in to purchase credits.');
        return;
      }
      setIsProcessing(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/credits/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          creditsRequested: selectedCredits,
          customerEmail: user.email,
          customerName: `${user.firstName} ${user.lastName}`,
          currency: currentCurrency
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Failed to initialize checkout');
      if (result.data.checkoutUrl) window.location.href = result.data.checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'An error occurred during secure checkout.');
    } finally {
      setIsProcessing(false);
    }
  };

  const steps = [
    { title: "Choose Fuel", desc: "Select credits based on your goals.", icon: <Zap /> },
    { title: "Automate Scrape", desc: "Start extraction in seconds.", icon: <Search /> },
    { title: "Export Leads", desc: "Download clean, verified CSVs.", icon: <Download /> },
  ];

  const faqs = [
    { q: "Do my credits ever expire?", a: "Never! Your credits stay in your account forever until you use them for a scrape." },
    { q: "How many leads per credit?", a: "1 Credit powers 1 complete scrape job, potentially extracting thousands of emails depending on the target account size." },
    { q: "Can I get a refund for unused credits?", a: "We offer refunds for unused credits within 14 days of purchase. Contact our support team for help." },
    { q: "Is the payment secure?", a: "Yes, all payments are processed securely via Cryptomus (cryptocurrency payment gateway) with SSL encryption." },
  ];

  return (
    <div className="min-h-screen pb-32 relative font-sans overflow-hidden bg-slate-50/30">
      
      {/* Background Atmosphere */}
      <div className="fixed inset-0 pointer-events-none -z-10 bg-white">
        <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-from),_transparent_70%)] from-violet-100/30 opacity-70 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[60%] h-[60%] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-from),_transparent_70%)] from-fuchsia-100/30 opacity-70 blur-[120px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent shadow-sm" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 space-y-8 lg:space-y-12 relative z-10">
        
        {/* --- 2-Column Hero --- */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
                <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
                    className="inline-flex items-center gap-2 px-3 py-1 bg-violet-600/5 border border-violet-600/10 rounded-full mb-6">
                    <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                    <span className="text-[10px] uppercase font-black tracking-[0.2em] text-violet-600">Enterprise Data Fuel</span>
                </motion.div>
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight leading-tight mb-6 font-outfit">
                    Fuel Your <br/>
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-400">
                        Growth.
                    </span>
                </h1>
                <p className="text-gray-500 font-medium text-lg leading-relaxed">
                    Stop waiting for monthly resets. Load credits instantly and scale your outreach at the speed of light.
                </p>
            </div>

            <div className="bg-white/50 backdrop-blur-xl border border-white rounded-[2rem] p-8 shadow-2xl shadow-violet-900/5 relative group overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-500/10 blur-3xl rounded-full" />
                <div className="flex items-center justify-between relative z-10">
                    <div className="space-y-1">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Balance</p>
                        <p className="text-3xl font-bold text-gray-900 font-outfit tabular-nums">{user?.creditsRemaining || 0} Credits</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white shadow-lg">
                            <Coins size={24} />
                        </div>
                        {/* Currency Selector */}
                        <div className="relative">
                            <select 
                                value={currentCurrency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="appearance-none bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-[10px] font-bold text-gray-600 outline-none focus:border-violet-500 transition-all pr-8 cursor-pointer"
                            >
                                {prices.map(p => (
                                    <option key={p.currencyCode} value={p.currencyCode}>{p.currencyCode}</option>
                                ))}
                                {prices.length === 0 && <option value="USD">USD</option>}
                            </select>
                            <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
                        </div>
                    </div>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-100 flex items-center justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest relative z-10">
                    <span>1 Credit = 1 Scrape</span>
                    <span className="text-emerald-500 flex items-center gap-1.5"><Shield size={12} /> SECURED BY CRYPTOMUS</span>
                </div>
            </div>
        </section>

        {/* --- Main Advanced Recharge Section (Modified for Admins) --- */}
        <div id="buy" className="w-full flex flex-col items-center gap-12">
           {!user?.isAdmin ? (
               <RechargeCard onPurchase={handlePurchase} isLoading={isProcessing} />
           ) : (
               <div className="w-full max-w-4xl p-12 rounded-[3rem] bg-slate-950 border border-white/10 shadow-2xl relative overflow-hidden text-center group">
                    {/* Background Accents */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-600/20 blur-[100px] rounded-full group-hover:bg-violet-600/30 transition-colors duration-700" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-fuchsia-600/10 blur-[100px] rounded-full group-hover:bg-fuchsia-600/20 transition-colors duration-700" />
                    
                    <div className="relative z-10 space-y-6">
                        <div className="w-20 h-20 rounded-[2rem] bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 shadow-inner mx-auto mb-8">
                            <Zap size={40} className="text-fuchsia-400 fill-fuchsia-400/20" />
                        </div>
                        <h2 className="text-4xl font-bold font-outfit text-white tracking-tight">Unlimited Fuel Enabled</h2>
                        <p className="text-slate-400 max-w-lg mx-auto text-lg">
                            Admin Account detected. Your scraping capabilities are unrestricted. Load, scrape, and export without limits.
                        </p>
                        <div className="pt-4">
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs font-bold text-fuchsia-400 uppercase tracking-widest">
                                <Shield size={14} /> Administrator Access Only
                            </span>
                        </div>
                    </div>
               </div>
           )}
           
           <AnimatePresence>
            {error && (
                <motion.div initial={{ opacity:0, scale: 0.98 }} animate={{ opacity:1, scale: 1 }} exit={{ opacity:0, scale:0.95 }} 
                className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl shadow-sm text-sm font-semibold max-w-xl">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p>{error}</p>
                </motion.div>
            )}

            {success && (
                <motion.div initial={{ opacity:0, scale: 0.98 }} animate={{ opacity:1, scale: 1 }} exit={{ opacity:0, scale:0.95 }} 
                className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl shadow-sm text-sm font-semibold max-w-xl">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <p>{success}</p>
                </motion.div>
            )}
            </AnimatePresence>
        </div>

        {/* --- How it Works (Space Filler / Value Add) --- */}
        <section className="py-20 bg-gray-900 rounded-[3rem] text-white relative overflow-hidden px-10">
            <div className="absolute top-0 right-0 w-[50%] h-full bg-gradient-to-l from-violet-600/20 via-transparent to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/microfabrics.png')] opacity-10 pointer-events-none" />
            
            <div className="relative z-10 max-w-5xl mx-auto">
                <div className="mb-12 text-center space-y-3">
                    <h2 className="text-3xl font-bold font-outfit tracking-tight text-white">How it Works</h2>
                    <p className="text-slate-100 font-bold text-sm">From recharge to leads in three easy steps.</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {steps.map((step, i) => (
                        <div key={i} className="relative p-8 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group">
                            <div className="w-10 h-10 rounded-xl bg-violet-500/20 text-violet-400 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                {step.icon}
                            </div>
                            <h3 className="text-lg font-bold mb-2 font-outfit text-white">{step.title}</h3>
                            <p className="text-slate-100 font-bold text-sm leading-relaxed">{step.desc}</p>
                            <div className="absolute top-6 right-8 text-3xl font-black text-white/20">0{i+1}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* --- FAQ Section & Advantage --- */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            
            {/* The Credit Advantage */}
            <div className="lg:col-span-5 space-y-8">
                <div className="space-y-3">
                    <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-outfit">The Advantage.</h2>
                    <p className="text-gray-500 font-medium leading-relaxed text-sm">Why leading marketers choose our credit system over traditional subscriptions.</p>
                </div>
                
                <div className="space-y-4">
                    {[
                        { title: "No Committment", desc: "No monthly bills or recurring charges.", icon: <ShieldCheck className="text-emerald-500" size={18}/> },
                        { title: "Unused Credits Stay", desc: "Credits never expire, roll them over forever.", icon: <LayoutDashboard className="text-violet-500" size={18}/> },
                        { title: "Scale instantly", desc: "Buy 10 or 10,000 credits in a single click.", icon: <Rocket className="text-fuchsia-500" size={18}/> },
                    ].map((adv, i) => (
                        <div key={i} className="flex gap-4 p-5 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                                {adv.icon}
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-sm mb-0.5">{adv.title}</h4>
                                <p className="text-[11px] text-gray-500 font-medium">{adv.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Accordion FAQ */}
            <div className="lg:col-span-7 space-y-8">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center shadow-lg">
                        <HelpCircle size={18} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight font-outfit">Got Questions?</h2>
                </div>

                <div className="space-y-3">
                    {faqs.map((faq, i) => (
                        <div key={i} className="rounded-xl bg-white border border-gray-100 overflow-hidden shadow-sm">
                            <button 
                                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50/50 transition-colors"
                            >
                                <span className="font-bold text-gray-800 text-sm tracking-tight">{faq.q}</span>
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {openFaq === i && (
                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                        className="px-6 pb-5 pt-1 text-xs font-medium text-gray-500 leading-relaxed border-t border-gray-50 mt-1">
                                        {faq.a}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* --- Global Proof (Bottom Filler) --- */}
        <section className="pt-20 border-t border-gray-100">
             <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
                {[
                    { label: "Leads Scraped", value: "12M+" },
                    { label: "Active Marketers", value: "2,400+" },
                    { label: "Data Accuracy", value: "98.7%" },
                    { label: "Refund Guarantee", value: "14 Days" },
                ].map((stat, i) => (
                    <div key={i} className="space-y-1">
                        <p className="text-2xl font-bold text-gray-900 font-outfit leading-none">{stat.value}</p>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{stat.label}</p>
                    </div>
                ))}
            </div>
            <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 p-8 rounded-[2rem] bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-xl shadow-violet-900/10">
                <div className="space-y-1">
                    <h3 className="text-2xl font-bold font-outfit leading-tight">Ready to boost your pipeline?</h3>
                    <p className="text-white font-bold text-sm">Join 2,000+ top researchers extracting leads today.</p>
                </div>
                <button 
                    onClick={() => {
                        const el = document.getElementById('buy');
                        el?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="px-8 py-3.5 bg-white text-violet-600 rounded-xl font-bold text-sm shadow-xl hover:shadow-2xl hover:-translate-y-1 active:translate-y-0 transition-all flex items-center gap-2 group"
                >
                    Get Your First Fuel <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </section>

      </div>
    </div>
  );
}